import * as React from 'react'
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl'
import { Row, Column } from '../../../../../components/layout-row'
import Checklist from '../../../../../components/checklist'
import Textblock from '../../../../../components/textblock'
import styles from './features.module.scss'
import InvoicingImg from './invoicing.png'
import ReceiptsImg from './receipts.png'
import AnnualImg from './annual.png'
import ReportsImg from './reports.png'
import SupportImg from './support.png'
import SalariesImg from './salaries.png'
import Button from '../../../../../components/button'
import DoodleBackground from '../../../../../components/doodle-background'

interface IFeaturesProps {
  onCtaClick: () => void
}

export default function Features({ onCtaClick }: IFeaturesProps) {
  return (
    <div className={styles.root}>
      <div className={styles.outer}>
        <div className={styles.inner}>
          <div className={styles.header}>
            <FormattedMessage
              defaultMessage="Så här fungerar det"
              id="landing-pages.signup.features.heading"
              tagName="h2"
            />
          </div>

          <div className={styles.features}>
            <Row alignItems="center">
              <Column first limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="Använd Dooer Fakturering för att skapa och skicka era kundfakturor så skickas all information vidare för bokföring automatiskt"
                      id="landing-pages.signup.features.control-description"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Automatisk avprickning av betalningar"
                          id="landing-pages.signup.features.control-list-access"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Skicka med epost, SMS eller få en PDF"
                          id="landing-pages.signup.features.control-list-email-sms-or-pdf"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Betalning via bankgiro eller Swish"
                          id="landing-pages.signup.features.control-bankgiro-or-swish-payments"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Hantering av moms, rabatter och valuta"
                          id="landing-pages.signup.features.control-list-tax-sales-currency"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Egen design och fakturamallar"
                          id="landing-pages.signup.features.control-list-invoice-templates"
                          key="4"
                        />,
                        <FormattedMessage
                          defaultMessage="Sätt upp påminnelser och avgifter"
                          id="landing-pages.signup.features.control-list-reminders-and-fees"
                          key="5"
                        />,
                        <FormattedMessage
                          defaultMessage="Hantera kunder och produkter"
                          id="landing-pages.signup.features.control-list-customer-and-articles"
                          key="6"
                        />,
                        <FormattedMessage
                          defaultMessage="Få statistik och rapporter"
                          id="landing-pages.signup.features.control-list-get-statistics-and-reports"
                          key="7"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Fakturera era kunder"
                      id="landing-pages.signup.features.control-title"
                    />
                  }
                />
              </Column>
              <Column>
                <img alt="Dooer invoices" src={InvoicingImg} />
              </Column>
            </Row>
            <Seperator />
            <Row alignItems="center" top="second">
              <Column first>
                <img alt="Dooer receipts" src={ReceiptsImg} />
              </Column>
              <Column limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="Alla utgifter fotograferar ni och laddar upp till oss så läser vi in all viktig data och bokför dem"
                      id="landing-pages.signup.features.upload-and-book"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Vi sätter upp betalningar som du godkänner"
                          id="landing-pages.signup.features.confirm-payments"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Matchning mot banktransaktioner"
                          id="landing-pages.signup.features.easy-list-match-transactions"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Hantering av utlägg, representation, etc"
                          id="landing-pages.signup.features.easy-list-outlay-and-represantation-managing"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Allt bokförs av din ekonom"
                          id="landing-pages.signup.features.easy-list-booking-through-economist"
                          key="4"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Leverantörsfakturor och kvitton"
                      id="landing-pages.signup.features.easier-living-invoices-and-receipts"
                    />
                  }
                />
              </Column>
            </Row>
            <Seperator />
            <Row alignItems="center">
              <Column first limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="I vårt lönesystem hanterar du alla anställda, deras lön, semester och andra viktiga detaljer medans vi bokför allt"
                      id="landing-pages.signup.features.control-second-description"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Lönespecifikationer"
                          id="landing-pages.signup.features.control-list-salary-specifications"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Hantering av semester"
                          id="landing-pages.signup.features.control-list-vacation-managing"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Skatter"
                          id="landing-pages.signup.features.control-taxes"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Hantering av anställda"
                          id="landing-pages.signup.features.control-list-employee-managing"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Tim och månadslön"
                          id="landing-pages.signup.features.control-list-hour-and-monthly-salary"
                          key="4"
                        />,
                        <FormattedMessage
                          defaultMessage="Utbetalning av löner"
                          id="landing-pages.signup.features.control-list-salary-payments"
                          key="5"
                        />,
                        <FormattedMessage
                          defaultMessage="Arbetsgivardeklarationer"
                          id="landing-pages.signup.features.control-list-employer-declerations"
                          key="6"
                        />,
                        <FormattedMessage
                          defaultMessage="Förmåner, traktamente, etc"
                          id="landing-pages.signup.features.control-list-price-benefits-etc"
                          key="7"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Hantera lön och anställda"
                      id="landing-pages.signup.features.control-second-title"
                    />
                  }
                />
              </Column>
              <Column>
                <img alt="Dooer web advices" src={SalariesImg} />
              </Column>
            </Row>
            <Seperator />
            <Row alignItems="center" top="second">
              <Column first>
                <img alt="Dooer reports" src={ReportsImg} />
              </Column>
              <Column limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="Varje månad sammanställer vi rapporter, men ni kan även gå in när som helst och se hur det går för företaget"
                      id="landing-pages.signup.features.monthly-reports"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Balansrapport"
                          id="landing-pages.signup.features.balance-report"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Kundreskontra i Dooer fakturering"
                          id="landing-pages.signup.features.easy-list-receivables"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Resultatrapport"
                          id="landing-pages.signup.features.easy-list-result-report"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Rapporter till Skatteverket"
                          id="landing-pages.signup.features.easy-list-reports-to-irs"
                          key="4"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Rapportering"
                      id="landing-pages.signup.features.easier-living-reporting"
                    />
                  }
                />
              </Column>
            </Row>
            <Seperator />
            <Row alignItems="center">
              <Column first limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="I vår app kan du när som helst skicka meddelanden till din ekonom, du får även uppgifter så du slipper hålla reda på viktiga datum mm."
                      id="landing-pages.signup.features.control-third-description"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Meddelanden till oss och dina anställda"
                          id="landing-pages.signup.features.control-list-messages"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Rapportering till Skatteverket"
                          id="landing-pages.signup.features.control-list-irs-reports"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Inbetalning av skatt"
                          id="landing-pages.signup.features.control-tax-payments"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Påminnelser om viktiga händelser"
                          id="landing-pages.signup.features.control-list-reminders-about-important-occations"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="Dokument vi saknar"
                          id="landing-pages.signup.features.control-list-missing-documents"
                          key="4"
                        />,
                        <FormattedMessage
                          defaultMessage="Få rådgivning och hjälp"
                          id="landing-pages.signup.features.control-list-price-support"
                          key="5"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Hjälp och support"
                      id="landing-pages.signup.features.control-third-title"
                    />
                  }
                />
              </Column>
              <Column>
                <img alt="Dooer support" src={SupportImg} />
              </Column>
            </Row>
            <Seperator />
            <Row alignItems="center" top="second">
              <Column first>
                <img alt="Dooer annual" src={AnnualImg} />
              </Column>
              <Column limitWidth>
                <Textblock
                  description={
                    <FormattedHTMLMessage
                      defaultMessage="Varje år väljer du om du även vill att vi ska göra din årsredovisning, vi tar då fram allt åt dig till ett fast och fördelaktigt pris"
                      id="landing-pages.signup.features.year-end-report"
                    />
                  }
                  extra={
                    <Checklist
                      horizontal
                      items={[
                        <FormattedMessage
                          defaultMessage="Årsredovisning"
                          id="landing-pages.signup.year-end-report"
                          key="1"
                        />,
                        <FormattedMessage
                          defaultMessage="Utdelning"
                          id="landing-pages.signup.features.easy-list-distribution"
                          key="2"
                        />,
                        <FormattedMessage
                          defaultMessage="Inkomstdeklaration"
                          id="landing-pages.signup.features.easy-list-income-statement"
                          key="3"
                        />,
                        <FormattedMessage
                          defaultMessage="K10 och personlig skatteuträkning"
                          id="landing-pages.signup.features.easy-list-k10-and-personal-tax-calculation"
                          key="4"
                        />,
                      ]}
                    />
                  }
                  hideSeparator
                  title={
                    <FormattedMessage
                      defaultMessage="Årsredovisning"
                      id="landing-pages.signup.features.easier-living-year-end-report"
                    />
                  }
                />
              </Column>
            </Row>
          </div>

          <Button onClick={onCtaClick} variant="success">
            <FormattedMessage defaultMessage="Kontakta oss" id="landing-pages.signup.features.cta" />
          </Button>
        </div>
      </div>

      <DoodleBackground className={styles.doodleBackground} variant="bottomLeft" />
    </div>
  )
}

function Seperator() {
  return (
    <div className={styles.rowSeperator}>
      <div className={styles.line} />
      <div className={styles.circle} />
    </div>
  )
}
